import { Component, EventEmitter, HostListener, Input, Output, OnChanges, SimpleChanges, OnInit, AfterViewInit } from '@angular/core';
import { GetTimelineResult, Referee, RefereeStatus, ReferenceTimeKnown, ReferenceTypes } from '../../referencing.models';
import { HttpService } from "src/app/services/http.service";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";

@Component({
    selector: 'referencing-referee-form',
    templateUrl: './referencing-referee-form.component.html',
    styleUrls: ['./referencing-referee-form.component.scss'],
    standalone: false
})
export class ReferencingRefereeFormComponent implements OnChanges, OnInit, AfterViewInit {
  @Input() timeline: GetTimelineResult|null = null;
  @Input() referee: Referee|null = null;
  @Input() isEditForm = false;
  @Input() name?: string;
  @Input() company?: string;


  @Output() onSaveRefereeEvent: EventEmitter<Referee> = new EventEmitter();
  @Output() onBackButton = new EventEmitter();

  public activeTab: string = 'Reference';
  public RefereeStatus = RefereeStatus;
  public ReferenceTimeKnown = ReferenceTimeKnown;
  public ReferenceTypes = ReferenceTypes;
  public shouldShowEndMonthDropdown: boolean = false;
  public shouldShowEndYearDropdown: boolean = false;
  public shouldShowStartMonthDropdown: boolean = false;
  public shouldShowStartYearDropdown: boolean = false;
  public shouldShowTimeKnownDropdown: boolean = false;
  public months: number[] = Array(12).fill(0).map((_, i) => i);
  public years: number[] = Array(100).fill(0).map((_, i) => i + (new Date()).getUTCFullYear() - 99).reverse();
  public timeKnowns: ReferenceTimeKnown[] = [
    ReferenceTimeKnown.LessThanOne,
    ReferenceTimeKnown.OneToTwo,
    ReferenceTimeKnown.TwoToThree,
    ReferenceTimeKnown.ThreeToFour,
    ReferenceTimeKnown.FourToFive,
    ReferenceTimeKnown.FiveToSix,
    ReferenceTimeKnown.SixToSeven,
    ReferenceTimeKnown.SevenToEight,
    ReferenceTimeKnown.EightToNine,
    ReferenceTimeKnown.NineToTen,
    ReferenceTimeKnown.MoreThanTen
  ]

  public copyLinkNotificationText: string = 'Copy from link to clipboard';
  public copyLinkNotificationWidth: string = '200px';
  public copyLinkNotificationLeft: string = '-90px';

  public form: UntypedFormGroup;
  public showValidation: boolean = false;
  public endDateInvalid: boolean = false;
  public refereeToSave: Referee | null = null;
  public referenceTypes = Object.values(ReferenceTypes);
  public isCurrentRole = false;

  public formChanged: boolean = false;
  public isSaving : boolean = false;
  public isFormTouched = false;

  private inputNames: string [] = [
    'firstName',
    'lastName',
    'emailAddress',
    'jobTitle',
    'organisationName',
    'candidatePosition',
    'startDateMonth',
    'startDateYear',
    'endDateMonth',
    'endDateYear',
    'timeKnown',
    'relationship'
  ]

  constructor(
    private formBuilder: UntypedFormBuilder,
    private httpService: HttpService,
  )
  {
    window.scrollTo(0, 0);
    this.isSaving = false;

    this.form = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      emailAddress: ['', [Validators.required, Validators.email]],
      contactNumber: [''],
      jobTitle: [''],
      organisationName: [''],
      candidatePosition: [''],
      startDateMonth: [null],
      startDateYear: [null],
      endDateMonth: [null],
      endDateYear: [null],
      currentRole: [false],
      notes: [''],
      timeKnown: [''],
      relationship: ['']
    });
  }

  public ngOnInit(): void {
    this.form.valueChanges.subscribe(() => {
      this.isFormTouched = true;
    })
  }

  public ngAfterViewInit(): void {
    const backButton = document.querySelector("#backbutton");
    if(backButton){
      backButton.addEventListener("click", (event) => {
        if(this.isFormTouched){
          event.preventDefault();
          window.alert("Are you sure you want to exit? You have unsaved changes.");
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['name']){
      this.name = changes['name'].currentValue;
    }

    if (changes['referee']){
      this.form.controls['firstName'].setValue(this.referee?.firstName);
      this.form.controls['lastName'].setValue(this.referee?.lastName);
      this.form.controls['emailAddress'].setValue(this.referee?.emailAddress);
      this.form.controls['contactNumber'].setValue(this.referee?.contactNumber);
      this.form.controls['jobTitle'].setValue(this.referee?.jobTitle);
      this.form.controls['organisationName'].setValue(this.referee?.organisationName);
      this.form.controls['candidatePosition'].setValue(this.referee?.jobTitle);
      this.form.controls['startDateMonth'].setValue(this.referee?.startDateMonth);
      this.form.controls['startDateYear'].setValue(this.referee?.startDateYear);
      this.form.controls['endDateMonth'].setValue(this.referee?.endDateMonth);
      this.form.controls['endDateYear'].setValue(this.referee?.endDateYear);
      this.form.controls['currentRole'].setValue(this.referee?.currentRole);
      this.form.controls['notes'].setValue(this.referee?.notes);
      this.form.controls['timeKnown'].setValue(this.referee?.timeKnown);
      this.form.controls['relationship'].setValue(this.referee?.relationship);
      this.showValidation = false;

      if (this.referee?.referenceType === ReferenceTypes.Personal) {
        this.setFieldOptional('jobTitle');
        this.setFieldOptional('organisationName');
        this.setFieldOptional('candidatePosition');
        this.setFieldOptional('startDateMonth');
        this.setFieldOptional('startDateYear');
        this.setFieldRequired('timeKnown');
        this.setFieldRequired('relationship');
        this.setFieldOptional('endDateMonth');
        this.setFieldOptional('endDateYear');
      } else {
        this.setFieldRequired('jobTitle');
        this.setFieldRequired('organisationName');
        this.setFieldRequired('candidatePosition');
        this.setFieldRequired('startDateMonth');
        this.setFieldRequired('startDateYear');
        this.setFieldOptional('timeKnown');
        this.setFieldOptional('relationship');
        if (this.referee?.currentRole) {
          this.setFieldOptional('endDateMonth');
          this.setFieldOptional('endDateYear');
        } else {
          this.setFieldRequired('endDateMonth');
          this.setFieldRequired('endDateYear');
        }
      }
    }
  }

  setFieldOptional(fieldName: string) {
    var field = this.form.get(fieldName)!;
    field.clearValidators();
    field.updateValueAndValidity();
  }
  setFieldRequired(fieldName: string) {
    var field = this.form.get(fieldName)!;
    field.setValidators([Validators.required]);
    field.updateValueAndValidity();
  }

  setActiveTab(tabName: string) {
    this.activeTab = tabName;
  }

  getField(fieldName:string) {
    return this.form.controls[fieldName];
  }
  getFieldShowValidationMessage(fieldName:string) {
    return this.showValidation &&
      this.getField(fieldName)['invalid'];
  }

  getEndDateRequiredMessage(fieldName:string) {
    return this.showValidation &&
      this.getField(fieldName)['invalid'] &&
      this.endDateInvalid == false;
  }

  getEndDateValidationMessage(fieldName:string) {
    return this.showValidation &&
      this.getField(fieldName)['invalid'] &&
      this.endDateInvalid == true;
  }

  toggleTimeKnownDropdown($event: any) {
    this.shouldShowTimeKnownDropdown = !this.shouldShowTimeKnownDropdown;
    $event.stopPropagation()
  }

  toggleStartMonthDropdown($event: any) {
    this.shouldShowStartMonthDropdown = !this.shouldShowStartMonthDropdown;
    $event.stopPropagation()
  }

  toggleEndMonthDropdown($event: any) {
    this.shouldShowEndMonthDropdown = !this.shouldShowEndMonthDropdown;
    $event.stopPropagation()
  }

  toggleStartYearDropdown($event: any) {
    this.shouldShowStartYearDropdown = !this.shouldShowStartYearDropdown;
    $event.stopPropagation()
  }

  toggleEndYearDropdown($event: any) {
    this.shouldShowEndYearDropdown = !this.shouldShowEndYearDropdown;
    $event.stopPropagation()
  }

  monthToString(month: number|null) {
    switch(month) {
      case 0:
        return 'January';
      case 1:
        return 'February';
      case 2:
        return 'March';
      case 3:
        return 'April';
      case 4:
        return 'May';
      case 5:
        return 'June';
      case 6:
        return 'July';
      case 7:
        return 'August';
      case 8:
        return 'September';
      case 9:
        return 'October';
      case 10:
        return 'November';
      case 11:
        return 'December';
      case null:
      default:
        return 'Choose your month';
    }
  }

  getTimeKnown(tk: ReferenceTimeKnown|null):string {
    switch(tk) {
      case ReferenceTimeKnown.LessThanOne:
        return 'Less than 1 year';
      case ReferenceTimeKnown.OneToTwo:
        return '1-2 years';
      case ReferenceTimeKnown.TwoToThree:
        return '2-3 years';
      case ReferenceTimeKnown.ThreeToFour:
        return '3-4 years';
      case ReferenceTimeKnown.FourToFive:
        return '4-5 years';
      case ReferenceTimeKnown.FiveToSix:
        return '5-6 years';
      case ReferenceTimeKnown.SixToSeven:
        return '6-7 years';
      case ReferenceTimeKnown.SevenToEight:
        return '7-8 years';
      case ReferenceTimeKnown.EightToNine:
        return '8-9 years';
      case ReferenceTimeKnown.NineToTen:
        return '9-10 years';
      case ReferenceTimeKnown.MoreThanTen:
        return 'More than 10 years';
      case null:
      default:
        return 'Choose a date range';
    }
  }

  onRefereeFirstNameChange(event: Event) {
    const value = (event.target as HTMLInputElement).value.trim();
    this.referee!.firstName = value;
    this.form.controls['firstName'].setValue(value);
    this.formChanged = true;
  }

  onRefereeLastNameChange(event: Event) {
    const value = (event.target as HTMLInputElement).value.trim();
    this.referee!.lastName = value;
    this.form.controls['lastName'].setValue(value);
    this.formChanged = true;
  }

  onRefereeEmailChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.referee!.emailAddress = value;
    this.form.controls['emailAddress'].setValue(value);
    this.formChanged = true;
  }

  onRefereeContactNumberChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.referee!.contactNumber = value;
    this.form.controls['contactNumber'].setValue(value);
    this.formChanged = true;
  }

  onRefereeJobTitleChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.referee!.jobTitle = value;
    this.form.controls['jobTitle'].setValue(value);
    this.formChanged = true;
  }

  onRefereeOrganisationNameChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.referee!.organisationName = value;
    this.form.controls['organisationName'].setValue(value);
    this.formChanged = true;
  }

  onRefereeYourPositionChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.referee!.candidatePosition = value;
    this.form.controls['candidatePosition'].setValue(value);
    this.formChanged = true;
  }

  onRefereeStartMonthChange(num: number) {
    this.referee!.startDateMonth = num;
    this.form.controls['startDateMonth'].setValue(num);
    this.setEndDateValidity();
    this.formChanged = true;
  }

  onRefereeStartYearChange(num: number) {
    this.referee!.startDateYear = num;
    this.form.controls['startDateYear'].setValue(num);
    this.setEndDateValidity();
    this.formChanged = true;
  }

  onRefereeEndMonthChange(num: number) {
    this.referee!.endDateMonth = num;
    this.form.controls['endDateMonth'].setValue(num);
    this.setEndDateValidity();
    this.formChanged = true;
  }

  onRefereeEndYearChange(num: number) {
    this.referee!.endDateYear = num;
    this.form.controls['endDateYear'].setValue(num);
    this.setEndDateValidity();
    this.formChanged = true;
  }

  setEndDateValidity() {
    let startDate = new Date(this.getField('startDateYear').value, this.getField('startDateMonth').value, 1);
    let endDate = new Date(this.getField('endDateYear').value, this.getField('endDateMonth').value, 1);
    let endMonthHasValue = this.getField('endDateMonth').value != null;
    let endYearHasValue = this.getField('endDateYear').value != null
    this.endDateInvalid = endMonthHasValue && endYearHasValue && startDate > endDate;
    if (this.endDateInvalid) {
      this.form.controls['endDateMonth'].setErrors({'invalid': true});
      this.form.controls['endDateYear'].setErrors({'invalid': true});
    } else {
      if (endMonthHasValue) {
        this.form.controls['endDateMonth'].setErrors(null);
      }
      if (endYearHasValue) {
        this.form.controls['endDateYear'].setErrors(null);
      }
    }
  }

  onRefereeCurrentRoleChange(event: Event) {
    const value = Boolean((event.target as HTMLInputElement).checked);
    this.isCurrentRole = !this.isCurrentRole;

    this.referee!.currentRole = this.isCurrentRole;
    if(value && this.referee){
      this.referee.endDateMonth = null;
      this.form.controls['endDateMonth'].setValue(null);
      this.referee.endDateYear = null;
      this.form.controls['endDateYear'].setValue(null);
      this.endDateInvalid = false;
    }
    this.form.controls['currentRole'].setValue(value);
    if (value) {
      this.setFieldOptional('endDateMonth');
      this.setFieldOptional('endDateYear');
    } else {
      this.setFieldRequired('endDateMonth');
      this.setFieldRequired('endDateYear');
    }
    this.formChanged = true;
  }

  onRefereeNotesChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.referee!.notes = value;
    this.form.controls['notes'].setValue(value);
    this.formChanged = true;
  }

  onRefereeTimeKnownChange(tk: ReferenceTimeKnown) {
    this.referee!.timeKnown = tk;
    this.form.controls['timeKnown'].setValue(tk);
    this.formChanged = true;
  }

  onRefereeRelationshipChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.referee!.relationship = value;
    this.form.controls['relationship'].setValue(value);
    this.formChanged = true;
  }

  public backToRefereelist(): void {
    this.onBackButton.emit();
  }

  onClickSaveButton() {
    this.isSaving = true;
    if (this.form.status === 'INVALID') {
      this.isSaving = false;
      this.showValidation = true;

      // set defaults
      let top = 240;
      var element = document.getElementById("firstName");

      // find error fields. cursor focus on first error field
      let hasFoundErrorInput = false;
      for (var i = 0; i < this.inputNames.length; i++) {
        const error = this.getFieldShowValidationMessage(this.inputNames[i]);

        if (error) {
          element = document.getElementById(this.inputNames[i]);
          hasFoundErrorInput = true;
          if(element){
            element.focus();
          } else {
            window.scroll({
              top: top,
              left: 0,
              behavior: 'smooth'
            });
          }
          break;
        }
      }
    }
    else {
      this.refereeToSave = this.referee;
      this.refereeToSave = this.form.value;

      // need to set these manually as cannot be changed by user
      // so there is no formControl and therefore value in the form
      if(this.refereeToSave && this.referee){
        const id = this.referee.id;
        this.refereeToSave.id = id;
        this.refereeToSave.status = 1;
        this.refereeToSave.referenceType = this.referee.referenceType;
      }

      if(this.refereeToSave){
        this.onSaveRefereeEvent.emit(this.refereeToSave);
        this.isSaving = false;
      }
      this.form.reset();
    }
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if(this.formChanged) {
      $event.returnValue = "Any changes that you made may not be saved";
    }
  }

}
